import {
  Request as ProtoRequest,
  Request_Method,
} from '@skedulo/protobuf-web/dist/common/v1/request'
import { DataField as ProtoDataField } from '@skedulo/protobuf-web/dist/query/api/v1/api'
import {
  ActionExecutionModeBatch,
  ActionExecutionModeBulk,
  ActionExecutionModeSingle,
  ActionExecutionModeSingle_FailureMode,
  ActionUserInterface,
  EndProcessInterface as ProtoEndProcessInterface,
  StandardEndProcessInterface as ProtoStandardEndProcessInterface,
  CustomEndProcessInterface as ProtoCustomEndProcessInterface,
  UserInterface as ProtoUserInterface,
  StandardUserInterface as ProtoStandardUserInterface,
  CustomUserInterface as ProtoCustomUserInterface,
  SuccessResultTemplate as ProtoSuccessResultTemplate,
  SuccessResultTemplate_ContentType,
} from '@skedulo/protobuf-web/dist/ui/crud/v1/actions'
import { Visibility } from '@skedulo/protobuf-web/dist/ui/crud/v1/actions'
import { IAction } from '../types'
import {
  Action,
  BatchExecutionMode,
  ContentType,
  CustomActionUserInterface,
  CustomEndProcessInterface,
  CustomUserInterface,
  DataField,
  EndProcessInterface,
  EndProcessInterfaceType,
  ExecutionMode,
  ExecutionModeSingleFailure,
  ExecutionModeType,
  Method,
  Request,
  SingleExecutionMode,
  StandardEndProcessInterface,
  StandardUserInterface,
  SuccessResultTemplate,
  UserInterface,
  UserInterfaceType,
} from '../types/action'

const toSingle = (
  source: ExecutionMode,
): ActionExecutionModeSingle | undefined => {
  if (source.type === ExecutionModeType.SINGLE) {
    const mode = source as SingleExecutionMode
    return {
      failureMode: toFailureMode(mode.failureMode),
    }
  }
}

const toFailureMode = (
  source: ExecutionModeSingleFailure,
): ActionExecutionModeSingle_FailureMode => {
  switch (source) {
    case ExecutionModeSingleFailure.CANCEL_ON_FAILURE:
      return ActionExecutionModeSingle_FailureMode.CANCEL_ON_FAILURE
    case ExecutionModeSingleFailure.CONTINUE_ON_FAILURE:
      return ActionExecutionModeSingle_FailureMode.CONTINUE_ON_FAILURE
    default:
      return ActionExecutionModeSingle_FailureMode.UNRECOGNIZED
  }
}

const toBulk = (source: ExecutionMode): ActionExecutionModeBulk | undefined => {
  if (source.type === ExecutionModeType.BULK) {
    return source as ActionExecutionModeBulk
  }
}

const toBatch = (
  source: ExecutionMode,
): ActionExecutionModeBatch | undefined => {
  if (source.type === ExecutionModeType.BATCH) {
    return source as BatchExecutionMode as ActionExecutionModeBatch
  }
}

const toUi = (
  source: CustomActionUserInterface,
): ActionUserInterface | undefined => {
  return {
    refreshEnabled: source.refreshEnabled,
    runInBackground: source.runInBackground,
    setup: toUserInterface(source.setup),
    success: toEndProcessInterface(source.success),
    partialSuccess: toEndProcessInterface(source.partialSuccess),
    failure: toEndProcessInterface(source.failure),
  }
}

const toEndProcessInterface = (
  source: EndProcessInterface,
): ProtoEndProcessInterface | undefined => {
  return {
    standard: toStandardEndProcessInterface(source),
    custom: toCustomEndProcessInterface(source),
  }
}

const toStandardEndProcessInterface = (
  source: EndProcessInterface,
): ProtoStandardEndProcessInterface | undefined => {
  if (source.type === EndProcessInterfaceType.STANDARD) {
    return source as StandardEndProcessInterface as ProtoStandardEndProcessInterface
  }
}

const toCustomEndProcessInterface = (
  source: EndProcessInterface,
): ProtoCustomEndProcessInterface | undefined => {
  if (source.type === EndProcessInterfaceType.CUSTOM) {
    return source as CustomEndProcessInterface as ProtoCustomEndProcessInterface
  }
}

const toUserInterface = (source: UserInterface): ProtoUserInterface => {
  return {
    standard: toStandardUserInterface(source),
    custom: toCustomUserInterface(source),
  }
}

const toStandardUserInterface = (
  source: UserInterface,
): ProtoStandardUserInterface | undefined => {
  if (source.type === UserInterfaceType.STANDARD) {
    return source as StandardUserInterface as ProtoStandardUserInterface
  }
}

const toCustomUserInterface = (
  source: UserInterface,
): ProtoCustomUserInterface | undefined => {
  if (source.type === UserInterfaceType.CUSTOM) {
    return source as CustomUserInterface as ProtoCustomUserInterface
  }
}

const toSuccessResultTemplate = (
  source?: SuccessResultTemplate,
): ProtoSuccessResultTemplate | undefined => {
  if (source) {
    return {
      contentType: toContentType(source.contentType),
      template: source.template || '',
    }
  }
}

const toContentType = (
  source?: ContentType,
): SuccessResultTemplate_ContentType => {
  switch (source) {
    case ContentType.TEXT_PLAIN:
      return SuccessResultTemplate_ContentType.TEXT_PLAIN
    case ContentType.APPLICATION_JSON:
      return SuccessResultTemplate_ContentType.APPLICATION_JSON
    default:
      return SuccessResultTemplate_ContentType.UNRECOGNIZED
  }
}

const toRequest = (source: Request): ProtoRequest => {
  return {
    url: source.url,
    method: toRequestMethod(source.method),
    body: source.body,
    headers: source.headers || {},
  }
}

const toRequestMethod = (source: Method): Request_Method => {
  switch (source) {
    case Method.GET:
      return Request_Method.GET
    case Method.POST:
      return Request_Method.POST
    case Method.PUT:
      return Request_Method.PUT
    case Method.PATCH:
      return Request_Method.PATCH
    case Method.DELETE:
      return Request_Method.DELETE
    default:
      return Request_Method.UNRECOGNIZED
  }
}

const toDataFields = (source: DataField[] | undefined): ProtoDataField[] => {
  return (source as ProtoDataField[]) || []
}

export function toProto(source: Action): IAction {
  return {
    id: source.uid,
    resourceName: source.objectName,
    name: source.name,
    label: source.label,
    description: source.description,
    request: toRequest(source.request),
    single: toSingle(source.executionMode),
    bulk: toBulk(source.executionMode),
    batch: toBatch(source.executionMode),
    ui: toUi(source.ui),
    fields: toDataFields(source.fields),
    limit: source.limit,
    successResultTemplate: toSuccessResultTemplate(
      source.successResultTemplate,
    ),
    securityConfig: source.securityConfig,
    visibility: Visibility.VISIBILITY_UNSPECIFIED,
    isSystemGenerated: source.isSystemGenerated,
    systemInformation: undefined,
    metadata: source.metadata,
  }
}
